import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { Lightbulb, ListChecks } from 'lucide-react';

import type { UploadResult } from '@/components/FileUpload';

import { QuestionImagesEditor } from './QuestionImagesEditor';
import { TextUnitEditor } from './TextUnitEditor';
import type { EditableQuestionImage } from './editorTypes';
import styles from './HintSolutionEditor.module.css';

type PanelId = 'hint' | 'solution';

interface HintSolutionEditorProps {
  detailedAnswerHtml?: string;
  detailedAnswerImages?: EditableQuestionImage[];
  disabled?: boolean;
  hintHtml?: string;
  hintImages?: EditableQuestionImage[];
  onDetailedAnswerChange: (value: string) => void;
  onDetailedAnswerImageChange?: (
    id: string,
    updates: Partial<EditableQuestionImage>,
  ) => void;
  onDetailedAnswerImageRemove?: (id: string) => void;
  onDetailedAnswerImageUpload?: (result: UploadResult) => void;
  onHintChange: (value: string) => void;
  onHintImageChange?: (id: string, updates: Partial<EditableQuestionImage>) => void;
  onHintImageRemove?: (id: string) => void;
  onHintImageUpload?: (result: UploadResult) => void;
  // Each image upload's start and end, the hint's and the solution's alike.
  // An upload goes on when its tab is left, and still reports its end.
  onImageUploadEnd?: () => void;
  onImageUploadStart?: () => void;
}

const PANELS: Array<{ icon: typeof Lightbulb; id: PanelId; label: string }> = [
  { icon: Lightbulb, id: 'hint', label: 'Hint' },
  { icon: ListChecks, id: 'solution', label: 'Worked Solution' },
];

function hasContent(html: string | undefined, images: EditableQuestionImage[]) {
  return Boolean(html?.replace(/<[^>]*>/g, '').trim()) || images.length > 0;
}

/**
 * The hint and worked solution, as tabs.
 *
 * Mirrors how a student meets them — the question info panel presents hint and
 * solution as tabs — so an instructor authors in the shape the reader sees, and
 * only one editor is on screen at a time instead of two stacked accordions.
 *
 * Both are `ContentSection`s in the proto and can hold images. The image editors
 * are not decoration: without them an authored image in a hint was silently
 * dropped the next time the question was saved.
 */
export function HintSolutionEditor({
  detailedAnswerHtml,
  detailedAnswerImages = [],
  disabled = false,
  hintHtml,
  hintImages = [],
  onDetailedAnswerChange,
  onDetailedAnswerImageChange,
  onDetailedAnswerImageRemove,
  onDetailedAnswerImageUpload,
  onHintChange,
  onHintImageChange,
  onHintImageRemove,
  onHintImageUpload,
  onImageUploadEnd,
  onImageUploadStart,
}: HintSolutionEditorProps) {
  const baseId = useId();
  const [activePanel, setActivePanel] = useState<PanelId | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const filled: Record<PanelId, boolean> = {
    hint: hasContent(hintHtml, hintImages),
    solution: hasContent(detailedAnswerHtml, detailedAnswerImages),
  };

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const move = (next: number) => {
      event.preventDefault();
      tabRefs.current[(next + PANELS.length) % PANELS.length]?.focus();
    };

    if (event.key === 'ArrowRight') {
      move(index + 1);
    } else if (event.key === 'ArrowLeft') {
      move(index - 1);
    } else if (event.key === 'Home') {
      move(0);
    } else if (event.key === 'End') {
      move(PANELS.length - 1);
    }
  }

  return (
    <div className={styles.root}>
      <div aria-label="Hint and solution" className={styles.tabs} role="tablist">
        {PANELS.map((panel, index) => {
          const isActive = activePanel === panel.id;
          const Icon = panel.icon;

          return (
            <button
              aria-controls={`${baseId}-${panel.id}-panel`}
              aria-selected={isActive}
              className={styles.tab}
              data-active={isActive ? 'true' : 'false'}
              id={`${baseId}-${panel.id}-tab`}
              key={panel.id}
              onClick={() => {
                // Selecting the open tab closes it, so a tall editor can be put
                // away again rather than only swapped.
                setActivePanel(isActive ? null : panel.id);
              }}
              onKeyDown={(event) => {
                handleKeyDown(event, index);
              }}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              role="tab"
              tabIndex={isActive || activePanel === null ? 0 : -1}
              type="button"
            >
              <Icon aria-hidden="true" size={15} strokeWidth={2} />
              <span>{panel.label}</span>
              {/* Which of the two already has something, without opening both. */}
              {filled[panel.id] ? (
                <span className={styles.filled} title="Has content">
                  <span className={styles.visuallyHidden}>Has content</span>
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {activePanel === 'hint' ? (
        <div
          aria-labelledby={`${baseId}-hint-tab`}
          className={styles.panel}
          id={`${baseId}-hint-panel`}
          role="tabpanel"
        >
          <TextUnitEditor
            label="Hint"
            onChange={onHintChange}
            placeholder="Write a hint…"
            value={hintHtml ?? '<p></p>'}
          />
          {onHintImageChange && onHintImageRemove && onHintImageUpload ? (
            <QuestionImagesEditor
              context="hint"
              description="Images shown with the hint, in the order listed."
              disabled={disabled}
              heading="Hint images"
              images={hintImages}
              onImageChange={onHintImageChange}
              onRemoveImage={onHintImageRemove}
              onUploadComplete={onHintImageUpload}
              onUploadEnd={onImageUploadEnd}
              onUploadStart={onImageUploadStart}
            />
          ) : null}
        </div>
      ) : null}

      {activePanel === 'solution' ? (
        <div
          aria-labelledby={`${baseId}-solution-tab`}
          className={styles.panel}
          id={`${baseId}-solution-panel`}
          role="tabpanel"
        >
          <TextUnitEditor
            label="Worked solution"
            onChange={onDetailedAnswerChange}
            placeholder="Write the worked solution…"
            value={detailedAnswerHtml ?? '<p></p>'}
          />
          {onDetailedAnswerImageChange &&
          onDetailedAnswerImageRemove &&
          onDetailedAnswerImageUpload ? (
            <QuestionImagesEditor
              context="solution"
              description="Images shown with the worked solution, in the order listed."
              disabled={disabled}
              heading="Solution images"
              images={detailedAnswerImages}
              onImageChange={onDetailedAnswerImageChange}
              onRemoveImage={onDetailedAnswerImageRemove}
              onUploadComplete={onDetailedAnswerImageUpload}
              onUploadEnd={onImageUploadEnd}
              onUploadStart={onImageUploadStart}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
